import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { compose, shouldUpdate } from 'recompose'
import { get, find, noop, isObject, isArray, isEqual, first } from 'lodash'

import { formatValue } from 'utils/formatters/value'
import { DropdownInfiniteList, DropdownInfiniteListWithSearch } from 'components/dropdown'
import { FormBlock } from 'components/formBlock/FormBlock'

import styles from './FormDropdown.module.scss'

const getDisplayValueForDropdown = ({ items = [], title = '' }) => {
  const firstItemTitle = get(first(items), 'title')
  const itemsLength = items.length

  return itemsLength > 1 ? `${itemsLength} ${title}` : firstItemTitle
}

const FormDropdownView = ({
  className,
  closeOnSelect,
  title,
  field,
  items,
  listMeta,
  size,
  placeholder,
  searchLimit,
  searchString,
  searchPlaceholder,
  keyName = 'id',
  valueName = 'title',
  rowWidth,
  rowSelection,
  isAutoFocus,
  isDisabled,
  isMultiSelect,
  isLoading,
  isReadonly,
  isSearchable,
  showErrorText,
  triggerClassName,
  triggerElementRenderer,
  rowTemplate,
  onChange,
  onOverlayHide,
  handleFieldChange,
  handleSearch,
  handleLoadMore,
}) => {
  const key = get(field.value, keyName, field.value)
  const item = find(items, { [keyName]: key }) || field.value
  const handleChange = item =>
    onChange ? onChange(item) : handleFieldChange ? handleFieldChange(field.name)(get(item, keyName)) : noop
  // left custom value only for `readonlyView` prop

  let value

  if (isArray(item)) {
    value = getDisplayValueForDropdown({ items: item, title })
  } else if (isObject(item)) {
    value = get(item, valueName)
  } else {
    value = item
  }

  return (
    <FormBlock
      className={classNames(styles.container, className)}
      title={title}
      readonlyView={formatValue(value)}
      isReadonly={!!isReadonly}
      isRequired={!!field.isRequired}>
      {handleSearch && (
        <DropdownInfiniteListWithSearch
          value={item}
          error={field.error}
          listItems={items}
          size={size}
          placeholder={placeholder || `Select ${title}`}
          searchLimit={searchLimit}
          searchPlaceholder={searchPlaceholder}
          multiSelectTitle={title}
          searchString={searchString}
          titleKey={valueName}
          listMaxHeight={279}
          width={rowWidth}
          isAutoFocus={isAutoFocus}
          isDisabled={isDisabled}
          isMultiSelect={isMultiSelect}
          isLoading={isLoading}
          isSearchable={isSearchable}
          showErrorText={showErrorText}
          isRowSelectedFn={
            rowSelection ? rowSelection : ({ item }) => key === get(item, keyName) || key === get(item, valueName)
          }
          triggerClassName={triggerClassName}
          triggerElementRenderer={triggerElementRenderer}
          rowRenderer={rowTemplate}
          onClose={onOverlayHide}
          onRowClick={({ item }) => handleChange(item)}
          onSearch={handleSearch}
          closeOnSelect={closeOnSelect}
          onLoadMore={handleLoadMore}
          listMeta={listMeta}
        />
      )}

      {!handleSearch && (
        <DropdownInfiniteList
          value={item}
          error={field.error}
          listItems={items}
          size={size}
          placeholder={placeholder || `Select ${title}`}
          multiSelectTitle={title}
          titleKey={valueName}
          listMaxHeight={279}
          width={rowWidth}
          isDisabled={isDisabled}
          isLoading={isLoading}
          isMultiSelect={isMultiSelect}
          showErrorText={showErrorText}
          isRowSelectedFn={
            rowSelection ? rowSelection : ({ item }) => key === get(item, keyName) || key === get(item, valueName)
          }
          triggerClassName={triggerClassName}
          triggerElementRenderer={triggerElementRenderer}
          rowRenderer={rowTemplate}
          onClose={onOverlayHide}
          onRowClick={({ item }) => handleChange(item)}
          closeOnSelect={closeOnSelect}
        />
      )}
    </FormBlock>
  )
}

FormDropdownView.propTypes = {
  className: PropTypes.string,
  closeOnSelect: PropTypes.bool,
  title: PropTypes.node,
  field: PropTypes.object.isRequired,
  items: PropTypes.array,
  listMeta: PropTypes.object,
  size: PropTypes.string,
  placeholder: PropTypes.string,
  searchLimit: PropTypes.number,
  searchString: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  keyName: PropTypes.string,
  valueName: PropTypes.string,
  rowWidth: PropTypes.number,
  rowSelection: PropTypes.func,
  isAutoFocus: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isMultiSelect: PropTypes.bool,
  isLoading: PropTypes.bool,
  isReadonly: PropTypes.bool,
  isSearchable: PropTypes.bool,
  showErrorText: PropTypes.bool,
  triggerClassName: PropTypes.string,
  triggerElementRenderer: PropTypes.func,
  rowTemplate: PropTypes.func,
  onChange: PropTypes.func,
  onOverlayHide: PropTypes.func,
  handleFieldChange: PropTypes.func,
  handleSearch: PropTypes.func,
  handleLoadMore: PropTypes.func,
}

export const FormDropdown = compose(shouldUpdate((props, nextProps) => !isEqual(props, nextProps)))(FormDropdownView)

FormDropdown.propTypes = {
  className: PropTypes.string,
  closeOnSelect: PropTypes.bool,
  title: PropTypes.node,
  field: PropTypes.object.isRequired,
  items: PropTypes.array,
  listMeta: PropTypes.object,
  size: PropTypes.string,
  placeholder: PropTypes.string,
  searchLimit: PropTypes.number,
  searchString: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  keyName: PropTypes.string,
  valueName: PropTypes.string,
  rowWidth: PropTypes.number,
  rowSelection: PropTypes.func,
  isAutoFocus: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isMultiSelect: PropTypes.bool,
  isLoading: PropTypes.bool,
  isReadonly: PropTypes.bool,
  isSearchable: PropTypes.bool,
  showErrorText: PropTypes.bool,
  triggerClassName: PropTypes.string,
  triggerElementRenderer: PropTypes.func,
  rowTemplate: PropTypes.func,
  onChange: PropTypes.func,
  onOverlayHide: PropTypes.func,
  handleFieldChange: PropTypes.func,
  handleSearch: PropTypes.func,
  handleLoadMore: PropTypes.func,
}
